import { useCallback, useRef, useState } from 'react'
import { isOk, postProgress } from '../../helpers/trainingApi'
import useActiveTime from './useActiveTime'

// Owns the learner's per-screen progress and every call that changes it.
//
// Calls go out strictly one at a time. The server reads the whole progress
// record, changes one screen and writes it back, so two calls in flight at
// once could overwrite each other. Each call also carries the active
// seconds counted since the previous one, which keeps time on task
// attributed to the screen it was spent on.
//
// `send` always resolves: with the response, or `{ status: 0 }` when the
// network failed.
const useProgressSender = (enrolmentId, initialBlocks, currentBlockId) => {
  const [blocks, setBlocks] = useState(initialBlocks ?? {})
  const blocksRef = useRef(blocks)
  const chain = useRef(Promise.resolve())
  const currentRef = useRef(currentBlockId)
  currentRef.current = currentBlockId
  const timeRef = useRef({})

  const send = useCallback(
    (body) => {
      const run = async () => {
        const { take, restore } = timeRef.current
        const activeSeconds = take()
        if (body.event === 'heartbeat' && !activeSeconds) return null
        let res
        try {
          res = await postProgress(enrolmentId, activeSeconds ? { ...body, activeSeconds } : body)
        } catch {
          res = { status: 0 }
        }
        if (!isOk(res)) {
          restore(activeSeconds)
          return res
        }
        if (res.data?.block) {
          blocksRef.current = { ...blocksRef.current, [body.blockId]: res.data.block }
          setBlocks(blocksRef.current)
        }
        return res
      }
      const result = chain.current.then(run, run)
      chain.current = result.catch(() => {})
      return result
    },
    [enrolmentId],
  )

  const heartbeat = useCallback(() => {
    if (currentRef.current) send({ blockId: currentRef.current, event: 'heartbeat' })
  }, [send])

  const time = useActiveTime({ onFlush: heartbeat })
  timeRef.current = time

  return { blocks, blocksRef, send, heartbeat }
}

export default useProgressSender
